import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, MessageSquare } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { Spinner } from '@/components/ui/Spinner';
import SmsPhoneMockup from './SmsPhoneMockup';
import * as marketingSvc from '@/services/marketing.service';
import type { SmsMarketingTemplate } from '@/services/marketing.service';

export interface SmsTemplatePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (tpl: SmsMarketingTemplate) => void;
  zClassName?: string;
}

export default function SmsTemplatePickerModal({
  isOpen,
  onClose,
  onApply,
  zClassName,
}: SmsTemplatePickerModalProps) {
  const [search, setSearch] = useState('');
  const [selectedFolder, setSelectedFolder] = useState('');
  const [selected, setSelected] = useState<SmsMarketingTemplate | null>(null);

  const { data: foldersData } = useQuery({
    queryKey: ['marketing', 'sms-template-folders'],
    queryFn: () => marketingSvc.getSmsTemplateFolders(),
    enabled: isOpen,
    staleTime: 60_000,
  });
  const folders = useMemo(
    () => (foldersData ?? []).map((f) => f.name).sort((a, b) => a.localeCompare(b)),
    [foldersData],
  );

  const { data, isLoading } = useQuery({
    queryKey: ['marketing', 'sms-templates', { search, folder: selectedFolder }],
    queryFn: () =>
      marketingSvc.listSmsTemplates({
        search: search || undefined,
        folder: selectedFolder || undefined,
        perPage: 100,
      } as any),
    enabled: isOpen,
  });
  const templates = data?.data ?? [];

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setSelectedFolder('');
      setSelected(null);
    }
  }, [isOpen]);

  const handleApply = () => {
    if (!selected) return;
    onApply(selected);
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className={cn('fixed inset-0 z-[9000] flex items-center justify-center bg-black/50', zClassName)}
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="flex flex-col bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
        style={{ width: '92%', maxWidth: 960, height: '80vh', maxHeight: '80vh' }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 flex-shrink-0">
          <h3 className="text-base font-semibold text-slate-800">Select SMS Template</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Toolbar: folder selector + search */}
        <div className="flex items-stretch border-b border-slate-200 flex-shrink-0">
          <div className="w-[320px] min-w-[320px] px-3 py-2.5 border-r border-slate-200 flex items-center">
            <select
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-700 outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-200"
              value={selectedFolder}
              onChange={(e) => { setSelectedFolder(e.target.value); setSelected(null); }}
            >
              <option value="">All Folders</option>
              {folders.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-0 px-4 py-2.5 flex items-center relative">
            <Search className="absolute left-7 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <input
              type="search"
              placeholder="Search templates…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setSelected(null); }}
              className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-700 outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-200"
              autoComplete="off"
            />
          </div>
        </div>

        {/* Body: list + preview */}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Template list */}
          <div className="w-[320px] min-w-[320px] border-r border-slate-200 overflow-y-auto p-2.5 flex flex-col gap-1.5">
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Spinner className="h-6 w-6" />
              </div>
            ) : templates.length === 0 ? (
              <div className="text-center py-16 text-sm text-slate-400">
                {search ? 'No templates match your search.' : selectedFolder ? 'No templates in this folder.' : 'No SMS templates available.'}
              </div>
            ) : (
              templates.map((tpl) => {
                const mediaUrls = tpl.mediaUrls ?? [];
                const bodyLen = (tpl.messageBody ?? '').length;
                const isSelected = selected?.smsTemplateId === tpl.smsTemplateId;
                return (
                  <button
                    key={tpl.smsTemplateId}
                    type="button"
                    onClick={() => setSelected(tpl)}
                    className={cn(
                      'w-full text-left px-3.5 py-3 rounded-lg border-2 transition-all',
                      isSelected
                        ? 'border-sky-400 bg-sky-50 shadow-sm'
                        : 'border-slate-100 bg-white hover:border-sky-300',
                    )}
                  >
                    <div className="font-semibold text-sm text-slate-800 truncate">{tpl.name}</div>
                    {tpl.description && (
                      <div className="text-xs text-slate-500 mt-0.5 truncate">{tpl.description}</div>
                    )}
                    <div className="flex items-center gap-2 mt-1.5 text-xs text-slate-400">
                      <span>{bodyLen} chars</span>
                      {mediaUrls.length > 0 && <span>· {mediaUrls.length} media</span>}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Preview -- scale phone mockup to fit available height */}
          <PreviewPane selected={selected} />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-slate-200 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={!selected}
            className="px-4 py-2 text-sm font-medium text-white bg-sky-500 rounded-md hover:bg-sky-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Apply Template
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const PHONE_INTRINSIC_H = 600;

function PreviewPane({ selected }: { selected: SmsMarketingTemplate | null }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  const recalc = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const available = el.clientHeight - 32;
    setScale(available >= PHONE_INTRINSIC_H ? 1 : Math.max(0.45, available / PHONE_INTRINSIC_H));
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    recalc();
    const ro = new ResizeObserver(recalc);
    ro.observe(el);
    return () => ro.disconnect();
  }, [recalc]);

  return (
    <div ref={containerRef} className="flex-1 min-w-0 flex items-center justify-center bg-slate-50 overflow-hidden">
      {selected ? (
        <div style={{ transform: `scale(${scale})`, transformOrigin: 'center center' }}>
          <SmsPhoneMockup
            messageBody={selected.messageBody ?? ''}
            mediaUrls={selected.mediaUrls ?? []}
            headerText="Template Preview"
          />
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2 text-slate-400">
          <MessageSquare className="h-10 w-10" />
          <p className="text-sm">Select a template to preview</p>
        </div>
      )}
    </div>
  );
}
